import React, { useMemo, useCallback, memo, useEffect, useRef, useState } from 'react'
import ListOfProducts from '../components/product/productsList'
import ProductCard from '../components/product/productCard'
import {
    CircularProgress,
    Grid,
    Typography,
    FormControlLabel,
    Checkbox
} from '@material-ui/core'
import useMainContext from '../hooks/useMainContext'
import ProductAccordionList from '../components/product/productAccordionList'
import SearchInput from '../components/searchInput'
import ProductListGrouped from '../components/product/productListGrouped'

export const ListOfProductsWithData = memo(function ListOfProductsWithData({ publicData, setSelected = () => { } }) {
    const {
        productsFiltered,
        cart,
        addToCart,
        setCart,
        productGroups,
        config,
        search
    } = useMainContext();
    const [showGrouped, setShowGrouped] = useState(false);

    // Ref for the main container
    const mainContainerRef = useRef(null);

    // Scroll to top when search changes
    useEffect(() => {
        // Scroll top for main container
        if (mainContainerRef.current) {
            mainContainerRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [search]);

    const getProductsGrouped = useCallback(() => {
        if (!productGroups?.length) {
            return [];
        }

        const today = new Date().getDay();

        // Create a map of all active product IDs for efficient lookup
        const activeProductMap = new Map(
            productsFiltered
                .filter(product => product.active)
                .map(product => [product.id, product])
        );

        // Modify group mapping to use the activeProductMap
        const groupedProducts = productGroups.map(group => {
            // Find actual product objects for the group
            const groupProducts = group.products
                ? group.products
                    .map(productId => activeProductMap.get(productId))
                    .filter(product => product !== undefined)
                : [];

            return {
                isProductGroup: true,
                ...group,
                products: groupProducts
            };
        }).sort((a, b) => a.sort - b.sort);

        // Create a Set of all grouped product IDs for O(1) lookup
        const groupedProductIds = new Set();
        groupedProducts.forEach(group => {
            group.products.forEach(p => groupedProductIds.add(p.id));
        });

        // Find truly ungrouped products
        const ungroupedProducts = productsFiltered.filter(product =>
            product.active && !groupedProductIds.has(product.id)
        );

        // Add ungrouped products if exists
        if (ungroupedProducts.length) {
            groupedProducts.push({
                isProductGroup: false,
                name: "Otras opciones",
                nameVisible: true,
                products: ungroupedProducts,
                activeDays: [0, 1, 2, 3, 4, 5, 6],
            });
        }

        // Final filtering of active groups
        return groupedProducts.filter(group =>
            group.products.length > 0 &&
            group.activeDays?.includes(today)
        );
    }, [config, productGroups, productsFiltered]);

    // Memoize grouped products
    const productsGrouped = useMemo(getProductsGrouped, [getProductsGrouped]);

    const visibleProducts = useMemo(() => {
        if (!productGroups?.length) return productsFiltered.filter(p => p.active);

        const today = new Date().getDay();
        const activeGroupIds = new Set();
        const inactiveGroupIds = new Set();
        
        // Identify active and inactive groups
        productGroups.forEach(g => {
            if (g.activeDays?.includes(today)) {
                activeGroupIds.add(g.id);
            } else {
                inactiveGroupIds.add(g.id);
            }
        });

        // Map products to their groups
        // Assuming productGroup structure has a list of product IDs
        const productInActiveGroup = new Set();
        const productInAnyGroup = new Set();

        productGroups.forEach(g => {
            if (g.products) {
                g.products.forEach(pId => {
                    productInAnyGroup.add(pId);
                    if (activeGroupIds.has(g.id)) {
                        productInActiveGroup.add(pId);
                    }
                });
            }
        });

        return productsFiltered.filter(p => {
            if (!p.active) return false;
            // Visible if it's in an active group OR if it's not in any group
            return productInActiveGroup.has(p.id) || !productInAnyGroup.has(p.id);
        });

    }, [productsFiltered, productGroups]);

    // Memoize createSecondary function
    const createSecondary = useCallback((prod) => prod.ingredients, []);

    // Render loading state for public data
    if (publicData && productsFiltered.length === 0) {
        return (
            <Grid container direction="column" justify="center" alignItems="center" style={{ height: '70vh' }}>
                <Grid item>
                    <CircularProgress style={{ color: '#EC441B' }} />
                </Grid>
            </Grid>
        );
    }

    // Render for public data
    if (publicData) {
        return (
            <Grid
                ref={mainContainerRef}
                container
                spacing={0}
                direction="row"
                justify="center"
                alignItems="center"
            >
                {productsGrouped?.length <= 1
                    ? visibleProducts
                        .map(prod => (
                            <Grid item key={prod.id}>
                                <ProductCard
                                    key={prod.id}
                                    product={prod}
                                    cart={cart}
                                    setCart={setCart}
                                    addToCart={addToCart}
                                    width={'100%'}
                                />
                            </Grid>
                        ))
                    : <ProductListGrouped productsGrouped={productsGrouped} />
                }
                {search && visibleProducts.length === 0 && (
                    <Grid item>
                        <Typography variant='h6'>No se encontró {search} 😔</Typography>
                    </Grid>
                )}
            </Grid>
        );
    }

    // Render for non-public data (restored original behavior)
    return (
        <Grid ref={mainContainerRef} container>
            <Grid item xs={12} style={{ padding: 10 }}>
                <SearchInput show={true} />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showGrouped}
                            onChange={(e) => setShowGrouped(e.target.checked)}
                            name="showGrouped"
                            color="primary"
                        />
                    }
                    label="Mostrar agrupados"
                />
            </Grid>
            <Grid item xs={12}>
                {productsFiltered.length === 0 ? (
                    <p>No hay productos para mostrar</p>
                ) : showGrouped ? (
                    <ProductAccordionList productsGrouped={productsGrouped} setSelected={setSelected} />
                ) : (
                    <ListOfProducts
                        items={productsFiltered}
                        createSecondary={createSecondary}
                        setSelected={setSelected}
                    />
                )}
            </Grid>
        </Grid>
    );
});

export default ListOfProductsWithData;